/**
 * Plano do loop: quadros, tamanho escolhido e, para cada camada GOES, o
 * instante/URL de cada quadro. Calculado pelo controlador e consumido pelo
 * pré-carregamento, pelas camadas do mapa e pela reprodução.
 */
import { create } from 'zustand';
import { pickAvailableWidth } from '../goes/listing';
import { frameUrl, type ImageWidth } from '../goes/star';
import type { TrechoSemBase } from './semNoaa';

export interface PlanLayer {
  uid: string;
  productId: string;
  visible: boolean;
  /** Listagem do produto ainda não chegou: manter o que está na tela. */
  pending: boolean;
  /** Instante desta camada em cada quadro do loop (null = sem imagem nesse quadro). */
  times: (number | null)[];
  /** Tamanhos publicados (→ bytes) de cada quadro. */
  sizes: (Partial<Record<ImageWidth, number>> | null)[];
  /** URL de cada quadro na largura do loop. */
  urls: (string | null)[];
}

export interface LoopPlan {
  frames: number[];
  /** Largura dos quadros do loop (a baixar). */
  width: ImageWidth;
  /** Quantos quadros cabem no orçamento comprimido nessa largura. */
  capacity: number;
  /** Quadros pedidos pelo período/passo, antes do limite do orçamento. */
  requested: number;
  truncated: boolean;
  layers: PlanLayer[];
  /** Camadas visíveis que entram no orçamento. */
  layerCount: number;
  /** Download total estimado do loop (bytes). */
  bytesTotal: number;
  /**
   * A NOAA parou de publicar a base: o último quadro dela. Os quadros depois
   * deste seguem sem a base, com as outras camadas (semNoaa.ts). Null (ou
   * ausente) no normal.
   */
  semBaseDesde?: number | null;
  /**
   * Trechos em que a linha do tempo segue sem a base (o do fim e as faltas de
   * mais de 45 min no meio), da grade de semNoaa.ts. Vazio (ou ausente) no normal.
   */
  trechosSemBase?: TrechoSemBase[];
}

export interface PlanProgress {
  /** Imagens do loop com o arquivo baixado (nível comprimido). */
  downloaded: number;
  /** Imagens decodificadas (janela de reprodução). */
  decoded: number;
  failed: number;
  total: number;
  /** Bytes já baixados / total estimado do loop. */
  bytesDownloaded: number;
  bytesTotal: number;
}

/**
 * Situação de cada camada no quadro atual:
 * `ok` na tela · `loading` ainda baixando (mostra a anterior) ·
 * `none` sem imagem perto deste horário · `failed` a imagem não pôde ser baixada.
 * Nos dois últimos casos a camada fica escondida neste quadro.
 */
export type LayerFrameStatus = 'ok' | 'loading' | 'none' | 'failed';

interface PlanState {
  plan: LoopPlan | null;
  /** Largura mais nítida para o quadro parado (qualidade automática), ou null. */
  stillWidth: ImageWidth | null;
  progress: PlanProgress;
  /** A primeira imagem já apareceu no mapa. */
  firstFrameShown: boolean;
  /** Situação de cada camada (uid) no quadro atual. */
  frameStatus: Record<string, LayerFrameStatus>;
  /**
   * Largura em que a animação TOCA. Normalmente = `plan.width`; quando a
   * qualidade muda, continua na antiga até a nova estar toda baixada e a
   * janela decodificada — uma única troca de nitidez, sem travar.
   */
  playWidth: ImageWidth | null;
  /** Tamanho da janela de quadros decodificados. */
  decodeWindow: number;
  /**
   * Largura do BITMAP em que a animação toca (px). Pode ser menor que a do
   * arquivo (`playWidth`): o quadro é decodificado no tamanho que a tela
   * mostra (ver `decodeWidthFor`/`loopDecodeWidth`). Null antes do plano.
   */
  bitmapWidth: number | null;
}

export const usePlanStore = create<PlanState>()(() => ({
  plan: null,
  stillWidth: null,
  progress: { downloaded: 0, decoded: 0, failed: 0, total: 0, bytesDownloaded: 0, bytesTotal: 0 },
  firstFrameShown: false,
  frameStatus: {},
  playWidth: null,
  decodeWindow: 0,
  bitmapWidth: null,
}));

/** Largura em que a animação toca agora (ou a do plano). */
export function currentPlayWidth(): ImageWidth | null {
  const { playWidth, plan } = usePlanStore.getState();
  return playWidth ?? plan?.width ?? null;
}

/** URL do quadro `i` da camada na largura pedida (ou a publicada mais próxima). */
export function layerUrlAt(layer: PlanLayer, i: number, width: ImageWidth): string | null {
  const t = layer.times[i];
  const sizes = layer.sizes[i];
  if (t === null || t === undefined || !sizes) return null;
  const w = pickAvailableWidth(sizes, width);
  return w ? frameUrl(layer.productId, t, w) : null;
}

/** Largura efetiva usada numa URL de quadro ("…-1800x1080.jpg" → 1800). */
export function widthFromUrl(url: string): ImageWidth {
  const m = /-(\d+)x\d+\.jpg$/.exec(url);
  return (m ? Number(m[1]) : 1800) as ImageWidth;
}

/** Camadas que participam da exibição (visíveis e com listagem). */
export function activePlanLayers(plan: LoopPlan | null): PlanLayer[] {
  return plan ? plan.layers.filter((l) => l.visible && !l.pending) : [];
}
